import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewWillEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { chevronBackOutline } from 'ionicons/icons'
import {
  setUpdateInputRows,
  setPtIdVerified,
  setWitness1,
  setWitness2,
  setClinicalIndicators,
  setTransfusionConsent,
  setBloodProduct,
  setBloodProductType,
  setProductId,
  setSiteAdministered,
  setVolumeInfused,
  setUnitCompleted,
  setWarmedUsed,
  setTransfusionOrder,
  setRoute,
  setCrossMatched,
  saveBloodProductAdministration,
  setVolumeInfusedUnit,
  setImpliedEmergentConsentReason,
} from '../store/bloodProductAdministration.actions'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { setInputRows } from '../../../../components/procedure/store/procedure.actions'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import Procedure from '../../../../components/procedure/Procedure.component'
import { getBloodProductAdministrationSelector } from '../selectors/bloodProductAdministration.selector'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../../utilities/object.util'
import { initialBloodProductAdministrationState } from '../store/bloodProductAdministration.reducers'
import { setDeleteProcedure, setProcedureLoaderItem } from '../../../timeline/store/timeline.actions'
import { goBack } from '../../../../utilities/navigation.util'
import { getCurrentDate, getCurrentTime, randomNumber } from '../../../../utilities'
import { BloodProductAdministrationConstants } from '../store/bloodProductAdministration.constants'
import { ProcedureConstants } from '../../../../components/procedure/store/procedure.constants'
import { IOption } from '../../../../common/common.types'
import {
  getAddMedicationAdministeredOptionsSelector,
  getBloodProductAdministrationOptionsSelector,
} from '../../../options/selectors'
import { IServiceConfig } from '../../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import { IAlertState } from '../../../../components/alert/store/alert.types'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'

const BloodProductAdministration: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const bloodProductAdministration = useSelector(getBloodProductAdministrationSelector)
  const bloodProductAdministrationOptions = useSelector(getBloodProductAdministrationOptionsSelector)
  const { doseUnitOptions } = useSelector(getAddMedicationAdministeredOptionsSelector)
  const {
    ptIdVerified,
    witness1,
    witness2,
    clinicalIndicator,
    crossMatched,
    transfusionConsent,
    transfusionConsentDate,
    transfusionConsentTime,
    bloodProduct,
    productId,
    bloodProductType,
    unitExpDate,
    unitExpTime,
    siteAdministered,
    transfusionStartDate,
    transfusionStartTime,
    transfusionEndDate,
    transfusionEndTime,
    route,
    volumeInfused,
    unitCompleted,
    warmerUsed,
    transfusionOrder,
    impliedEmergentConsentReason,
    date,
    time,
    assessedBy,
    successfulAttempt,
    pta,
    procedureComplications,
    response,
    responsePNNV,
    comments,
    updateInputRows,
    attempts,
    rolePerforming,
    rolePerformingPNNV,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
    isEkgImport,
  } = bloodProductAdministration

  const {
    ptIdVerifiedOptions,
    clinicalIndicatorOptions,
    crossMatchedOptions,
    transfusionOrderOptions,
    bloodProductOptions,
    bloodProductRouteOptions,
    bloodProductTypeOptions,
    transfusionConsentOptions,
    impliedEmergentConsentReasonOptions,
  } = bloodProductAdministrationOptions

  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: t('bloodProductAdministration.DateTimeRequired') }))
      return false
    }
    return true
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = ['key', 'addToTimeline', 'updateInputRows', 'nemsisCode', 'snomedId', 'witness1', 'witness2']
    const currentState = filterOutObjectKeys(bloodProductAdministration, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialBloodProductAdministrationState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  const save = () => {
    dispatch(saveBloodProductAdministration())
  }

  const onPtIdVerifiedChange = (option: IOption | null) => {
    dispatch(setPtIdVerified(option))
    dispatch(setUpdateInputRows(true))
    save()
  }

  const onWitness1Change = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setWitness1(e.detail.value))
    save()
  }

  const onWitness2Change = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setWitness2(e.detail.value))
    save()
  }

  const onClinicalIndicatorsChange = (option: IOption | null) => {
    dispatch(setClinicalIndicators(option))
    save()
  }

  const onTransfusionConsentChange = (option: IOption | null) => {
    dispatch(setTransfusionConsent(option))
    save()
  }

  const onBloodProductChange = (option: IOption | null) => {
    dispatch(setBloodProduct(option))
    save()
  }

  const onBloodProductTypeOnChange = (option: IOption | null) => {
    dispatch(setBloodProductType(option))
    save()
  }

  const onProductIdChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setProductId(e.detail.value))
    save()
  }

  const onSiteAdministeredChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setSiteAdministered(e.detail.value))
    save()
  }

  const onVolumeInfusedChange = (values: string[]) => {
    const volumeInfused = values.join('')
    let volumeInfusedUnit = null
    if (volumeInfused) {
      const matchingUnit = doseUnitOptions?.find(
        ({ name }) => name?.toUpperCase() === BloodProductAdministrationConstants.VOLUME_INFUSED_UNIT.toUpperCase()
      )
      volumeInfusedUnit = matchingUnit
    }
    dispatch(setVolumeInfused(volumeInfused))
    dispatch(setVolumeInfusedUnit(volumeInfusedUnit))
    save()
  }

  const onTransfusionOrderChange = (option: IOption | null) => {
    dispatch(setTransfusionOrder(option))
    save()
  }

  const onImpliedEmergentConsentReasonChange = (option: IOption | null) => {
    dispatch(setImpliedEmergentConsentReason(option))
    save()
  }

  const onRouteChange = (option: IOption | null) => {
    dispatch(setRoute(option))
    save()
  }

  const onCrossMatchedChange = (option: IOption | null) => {
    dispatch(setCrossMatched(option))
    save()
  }

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'bloodProductAdministration/SET_TIME', time: time })
              dispatch({ type: 'bloodProductAdministration/SET_DATE', date: date })
              save()
              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: bloodProductAdministration.key,
                rowActionType: TimelineConstants.SET_BLOOD_PRODUCT_ADMINISTRATION,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      const dateTimeValidationCheck = initialStateCheck()
      if (dateTimeValidationCheck) {
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }
  const inputRows = [
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: BloodProductAdministrationConstants.SET_PT_ID_VERIFIED,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('bloodProductAdministration.PtIdVerified'),
            dataTestID: 'PtIdVerified',
            options: ptIdVerifiedOptions,
            action: onPtIdVerifiedChange,
            listHeader: t('bloodProductAdministration.PtIdVerified'),
            selectedOption: ptIdVerified,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: BloodProductAdministrationConstants.SET_WITNESS1,
          type: ProcedureConstants.TEXT,
          hidden: !ptIdVerified || ptIdVerified?.id !== 1,
          props: {
            label: t('bloodProductAdministration.Witness1'),
            dataTestID: 'Witness1',
            onChangeAction: onWitness1Change,
            listHeader: t('bloodProductAdministration.Witness1'),
            inputValue: witness1,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: BloodProductAdministrationConstants.SET_WITNESS2,
          type: ProcedureConstants.TEXT,
          hidden: !ptIdVerified || ptIdVerified?.id !== 1,
          props: {
            label: t('bloodProductAdministration.Witness2'),
            dataTestID: 'Witness2',
            onChangeAction: onWitness2Change,
            listHeader: t('bloodProductAdministration.Witness2'),
            inputValue: witness2,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: BloodProductAdministrationConstants.SET_CLINICAL_INDICATORS,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('bloodProductAdministration.ClinicalIndicator'),
            dataTestID: 'ClinicalIndicator',
            options: clinicalIndicatorOptions,
            action: onClinicalIndicatorsChange,
            listHeader: t('bloodProductAdministration.ClinicalIndicator'),
            selectedOption: clinicalIndicator,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: BloodProductAdministrationConstants.SET_CROSS_MATCHED,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('bloodProductAdministration.CrossMatched'),
            dataTestID: 'CrossMatched',
            action: onCrossMatchedChange,
            options: crossMatchedOptions,
            listHeader: t('bloodProductAdministration.CrossMatched'),
            selectedOption: crossMatched,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: BloodProductAdministrationConstants.SET_TRANSFUSION_CONSENT,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('bloodProductAdministration.TransfusionConsent'),
            dataTestID: 'TransfusionConsent',
            options: transfusionConsentOptions,
            action: onTransfusionConsentChange,
            listHeader: t('bloodProductAdministration.TransfusionConsent'),
            selectedOption: transfusionConsent,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: BloodProductAdministrationConstants.SET_TRANSFUSION_CONSENT_DATE_TIME,
          type: ProcedureConstants.TIMES_ROW,
          hidden: false,
          props: {
            elementId: 'TransfusionConsentDate',
            header: t('bloodProductAdministration.TransfusionConsentDate'),
            time: transfusionConsentTime,
            date: transfusionConsentDate,
            timeAction: BloodProductAdministrationConstants.SET_TRANSFUSION_CONSENT_TIME,
            dateAction: BloodProductAdministrationConstants.SET_TRANSFUSION_CONSENT_DATE,
            currentDateTimeAction: BloodProductAdministrationConstants.SET_TRANSFUSION_CONSENT_DATE_TIME,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: BloodProductAdministrationConstants.SET_BLOOD_PRODUCT_TYPE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('bloodProductAdministration.ProductBloodType'),
            dataTestID: 'ProductBloodType',
            options: bloodProductTypeOptions,
            action: onBloodProductTypeOnChange,
            listHeader: t('bloodProductAdministration.ProductBloodType'),
            selectedOption: bloodProductType,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: BloodProductAdministrationConstants.SET_BLOOD_PRODUCT,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('bloodProductAdministration.BloodProduct'),
            dataTestID: 'BloodProduct',
            options: bloodProductOptions,
            action: onBloodProductChange,
            listHeader: t('bloodProductAdministration.BloodProduct'),
            selectedOption: bloodProduct,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: BloodProductAdministrationConstants.SET_PRODUCT_ID,
          type: ProcedureConstants.TEXT,
          hidden: false,
          props: {
            label: t('bloodProductAdministration.ProductId'),
            dataTestID: 'ProductId',
            onChangeAction: onProductIdChange,
            listHeader: t('bloodProductAdministration.ProductId'),
            inputValue: productId,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: BloodProductAdministrationConstants.SET_UNIT_EXPIRY_DATE_TIME,
          type: ProcedureConstants.TIMES_ROW,
          hidden: false,
          props: {
            elementId: 'UnitExpiryDate',
            header: t('bloodProductAdministration.UnitExpiryDate'),
            time: unitExpTime,
            date: unitExpDate,
            timeAction: BloodProductAdministrationConstants.SET_UNIT_EXPIRY_TIME,
            dateAction: BloodProductAdministrationConstants.SET_UNIT_EXPIRY_DATE,
            currentDateTimeAction: BloodProductAdministrationConstants.SET_UNIT_EXPIRY_DATE_TIME,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: BloodProductAdministrationConstants.SET_ROUTE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('bloodProductAdministration.Route'),
            dataTestID: 'Route',
            action: onRouteChange,
            options: bloodProductRouteOptions,
            listHeader: t('bloodProductAdministration.Route'),
            selectedOption: route,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: BloodProductAdministrationConstants.SET_SITE_ADMINISTERED,
          type: ProcedureConstants.TEXT,
          hidden: false,
          props: {
            label: t('bloodProductAdministration.SiteAdministered'),
            dataTestID: 'SiteAdministered',
            onChangeAction: onSiteAdministeredChange,
            listHeader: t('bloodProductAdministration.SiteAdministered'),
            inputValue: siteAdministered,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: BloodProductAdministrationConstants.SET_TRANSFUSION_START_DATE_TIME,
          type: ProcedureConstants.TIMES_ROW,
          hidden: false,
          props: {
            elementId: 'TransfusionStartDate',
            header: t('bloodProductAdministration.TransfusionStartDate'),
            time: transfusionStartTime,
            date: transfusionStartDate,
            timeAction: BloodProductAdministrationConstants.SET_TRANSFUSION_START_TIME,
            dateAction: BloodProductAdministrationConstants.SET_TRANSFUSION_START_DATE,
            currentDateTimeAction: BloodProductAdministrationConstants.SET_TRANSFUSION_START_DATE_TIME,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: BloodProductAdministrationConstants.SET_TRANSFUSION_END_DATE_TIME,
          type: ProcedureConstants.TIMES_ROW,
          hidden: false,
          props: {
            elementId: 'TransfusionEndDate',
            header: t('bloodProductAdministration.TransfusionEndDate'),
            time: transfusionEndTime,
            date: transfusionEndDate,
            timeAction: BloodProductAdministrationConstants.SET_TRANSFUSION_END_TIME,
            dateAction: BloodProductAdministrationConstants.SET_TRANSFUSION_END_DATE,
            currentDateTimeAction: BloodProductAdministrationConstants.SET_TRANSFUSION_END_DATE_TIME,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: BloodProductAdministrationConstants.SET_VOLUME_INFUSED,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: false,
          props: {
            label: `${t('bloodProductAdministration.VolumeInfused')} (${
              BloodProductAdministrationConstants.VOLUME_INFUSED_UNIT
            })`,
            dataTestID: 'VolumeInfused',
            elementId: 'VolumeInfused',
            onNumPadClick: onVolumeInfusedChange,
            values: volumeInfused.split(''),
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: BloodProductAdministrationConstants.SET_UNIT_COMPLETED,
          type: ProcedureConstants.TOGGLE,
          hidden: false,
          props: {
            rightLabel: t('bloodProductAdministration.UnitCompleted'),
            dataTestId: 'UnitCompleted',
            isChecked: unitCompleted,
            onChangeAction: setUnitCompleted,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: BloodProductAdministrationConstants.SET_WARMER_USED,
          type: ProcedureConstants.TOGGLE,
          hidden: false,
          props: {
            rightLabel: t('bloodProductAdministration.WarmerUsed'),
            dataTestId: 'WarmerUsed',
            isChecked: warmerUsed,
            onChangeAction: setWarmedUsed,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: BloodProductAdministrationConstants.SET_TRANSFUSION_ORDER,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('bloodProductAdministration.TransfusionOrder'),
            dataTestID: 'TransfusionOrder',
            action: onTransfusionOrderChange,
            options: transfusionOrderOptions,
            listHeader: t('bloodProductAdministration.TransfusionOrder'),
            selectedOption: transfusionOrder,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: BloodProductAdministrationConstants.SET_IMPLIED_EMERGENT_CONSENT_REASON,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: !impliedEmergentConsentReasonOptions?.length,
          props: {
            label: t('bloodProductAdministration.ImpliedEmergentReason'),
            dataTestID: 'ImpliedEmergentReason',
            action: onImpliedEmergentConsentReasonChange,
            options: impliedEmergentConsentReasonOptions,
            listHeader: t('bloodProductAdministration.ImpliedEmergentReason'),
            selectedOption: impliedEmergentConsentReason,
          },
        },
      ],
    },
  ]

  useIonViewWillEnter(() => {
    dispatch(setUpdateInputRows(true))
  })

  if (updateInputRows) {
    dispatch(setInputRows(inputRows))
    dispatch(setUpdateInputRows(false))
  }

  return (
    <IonPage id='BloodProductAdministrationPage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>{t('bloodProductAdministration.BloodProductAdministration')}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen color='light'>
        <Procedure
          type={'bloodProductAdministration'}
          procedureDate={date}
          procedureTime={time}
          assessedBy={assessedBy}
          successfulAttempt={successfulAttempt}
          procedureComplications={procedureComplications}
          response={response}
          responsePNNV={responsePNNV}
          comments={comments}
          attempts={attempts}
          rolePerforming={rolePerforming}
          rolePerformingPNNV={rolePerformingPNNV}
          procedurePerformedByAnotherUnit={procedurePerformedByAnotherUnit}
          procedurePerformedByAnotherUnitPNNV={procedurePerformedByAnotherUnitPNNV}
          pta={pta}
          isEkgImport={isEkgImport}
        />
      </IonContent>
    </IonPage>
  )
}

export default BloodProductAdministration
